/**
 * InventoryImport — admin tab that lets the user pull products from the
 * external GemVault Dashboard and selectively import them into OpikJewels.
 *
 * Flow:
 *   1. First visit → empty state with a "Connect" form (URL + email + pwd).
 *   2. After connect → list of GemVault products with checkboxes. Status
 *      badges show whether each item is already imported.
 *   3. Tick the items + pick a currency → "Import Selected (N)" → bulk POST.
 *   4. On success → counts of created/updated and a re-fetched list.
 *
 * Reasoning notes:
 *   - We intentionally re-fetch the products list after every import so the
 *     "imported" flag updates immediately without optimistic UI gymnastics.
 *   - The currency picker exists because GemVault stores raw prices without
 *     a currency. The admin chooses what currency the seller meant.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link2, Unlink, Search, RefreshCw, Loader2, CheckCircle2 } from "lucide-react";
import api from "../../lib/api";

// (currency selector removed — IDR-only per pricing policy)

const StatusBadge = ({ imported }) =>
  imported ? (
    <span data-testid="status-imported" className="inline-flex items-center gap-1 text-[9px] font-accent tracking-[0.22em] uppercase text-emerald-400 border border-emerald-400/30 px-2 py-1">
      <CheckCircle2 size={10} /> Imported
    </span>
  ) : (
    <span data-testid="status-new" className="inline-flex items-center text-[9px] font-accent tracking-[0.22em] uppercase text-gold border border-gold/40 px-2 py-1">
      New
    </span>
  );

const InventoryImport = () => {
  const [status, setStatus] = useState(null);          // { connected, base_url, ... }
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  // Per-product custom price overrides. Keyed by GemVault inventory_id.
  // Empty/undefined value => use the GemVault selling_price as-is.
  const [priceOverrides, setPriceOverrides] = useState({});
  const [search, setSearch] = useState("");
  // Base currency is locked to IDR per the OPIKJEWELS pricing policy. The
  // selector is intentionally removed — every import is treated as IDR-base
  // so downstream FX conversion is consistent with manually-added products.
  const currency = "IDR";
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // Connect form (only used when not yet connected)
  const [form, setForm] = useState({
    base_url: "https://gem-vault-admin.preview.emergentagent.com",
    email: "",
    password: "",
  });

  const fetchStatus = async () => {
    try {
      const { data } = await api.get("/admin/inventory-import/status");
      setStatus(data);
      return data;
    } catch (err) {
      setStatus({ connected: false });
      return { connected: false };
    }
  };

  const fetchProducts = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/admin/inventory-import/products");
      setProducts(data.products || []);
      // Pre-fill price overrides with the CURRENT OpikJewels price for
      // already-imported products, so the admin can see and adjust without
      // typing the existing value from memory.
      setPriceOverrides((prev) => {
        const next = { ...prev };
        (data.products || []).forEach((p) => {
          if (next[p.inventory_id] !== undefined) return; // user already edited
          if (p.imported && p.opik_price != null) {
            next[p.inventory_id] = String(p.opik_price);
          }
        });
        return next;
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to fetch products from GemVault");
      setProducts([]);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    (async () => {
      const s = await fetchStatus();
      if (s?.connected) await fetchProducts();
      setLoading(false);
    })();
  }, []);

  const handleConnect = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/inventory-import/connect", form);
      toast.success("Connected to GemVault Dashboard");
      await fetchStatus();
      await fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Connection failed");
    } finally {
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect from GemVault? Already-imported products remain in your catalog.")) return;
    await api.delete("/admin/inventory-import/connect");
    setStatus({ connected: false });
    setProducts([]);
    setSelected(new Set());
    toast.success("Disconnected");
  };

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filtered = products.filter((p) =>
    !search.trim()
      ? true
      : `${p.name} ${p.sku} ${p.category}`.toLowerCase().includes(search.toLowerCase()),
  );

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((p) => selected.has(p.inventory_id));

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) filtered.forEach((p) => next.delete(p.inventory_id));
      else filtered.forEach((p) => next.add(p.inventory_id));
      return next;
    });
  };

  const handleImport = async () => {
    if (selected.size === 0) {
      toast.error("Pick at least one product to import.");
      return;
    }
    // Build the price_overrides map from non-empty inputs in `selected`.
    const overrides = {};
    Array.from(selected).forEach((id) => {
      const raw = priceOverrides[id];
      if (raw !== undefined && raw !== "" && !Number.isNaN(Number(raw))) {
        overrides[id] = Number(raw);
      }
    });
    setBusy(true);
    try {
      const { data } = await api.post("/admin/inventory-import/import", {
        inventory_ids: Array.from(selected),
        currency,
        price_overrides: overrides,
      });
      toast.success(
        `Imported · ${data.created} new · ${data.updated} updated${data.skipped?.length ? ` · ${data.skipped.length} skipped` : ""}`,
      );
      setSelected(new Set());
      await fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="inventory-import-loading" className="text-white/50 py-20 text-center">
        <Loader2 className="animate-spin inline-block mr-2" size={16} /> Loading…
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────────
  // CONNECT FORM
  // ────────────────────────────────────────────────────────────────────
  if (!status?.connected) {
    return (
      <div data-testid="inventory-import" className="max-w-xl mx-auto">
        <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-3">
          Connect External Inventory
        </div>
        <h2 className="font-heading text-3xl text-ivory mb-6">GemVault Dashboard</h2>
        <p className="text-white/55 text-sm mb-8 leading-relaxed">
          Connect your external <span className="text-ivory">GemVault Dashboard</span> to browse and selectively import its inventory into OpikJewels. Credentials are stored encrypted in your OpikJewels database and used only by the backend.
        </p>

        <form onSubmit={handleConnect} className="space-y-5 bg-onyx border border-gold/30 p-7">
          <div>
            <label className="font-accent text-[10px] tracking-[0.28em] uppercase text-white/45 mb-2 block">Dashboard URL</label>
            <input
              data-testid="connect-url"
              type="url"
              required
              value={form.base_url}
              onChange={(e) => setForm({ ...form, base_url: e.target.value })}
              className="bg-transparent border-b border-white/20 px-0 py-2 w-full focus:outline-none focus:border-gold text-ivory"
            />
          </div>
          <div>
            <label className="font-accent text-[10px] tracking-[0.28em] uppercase text-white/45 mb-2 block">Admin Email</label>
            <input
              data-testid="connect-email"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="bg-transparent border-b border-white/20 px-0 py-2 w-full focus:outline-none focus:border-gold text-ivory"
            />
          </div>
          <div>
            <label className="font-accent text-[10px] tracking-[0.28em] uppercase text-white/45 mb-2 block">Admin Password</label>
            <input
              data-testid="connect-password"
              type="password"
              required
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="bg-transparent border-b border-white/20 px-0 py-2 w-full focus:outline-none focus:border-gold text-ivory"
            />
          </div>
          <button
            data-testid="connect-submit"
            disabled={busy}
            className="w-full flex items-center justify-center gap-2 bg-gold text-black font-accent uppercase tracking-[0.28em] text-xs px-7 py-4 hover:bg-ivory transition-colors disabled:opacity-50"
          >
            {busy ? <Loader2 className="animate-spin" size={14} /> : <Link2 size={14} />}
            {busy ? "Connecting…" : "Connect"}
          </button>
        </form>
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────────
  // PRODUCT BROWSER
  // ────────────────────────────────────────────────────────────────────
  return (
    <div data-testid="inventory-import" className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-white/10 pb-5">
        <div>
          <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-2">Inventory Import</div>
          <h2 className="font-heading text-3xl text-ivory">From GemVault Dashboard</h2>
          <div className="text-white/40 text-xs mt-1">
            <span className="text-ivory">{status.base_url}</span> · {status.email} · {status.synced_count} already imported
          </div>
        </div>
        <button
          data-testid="disconnect-btn"
          onClick={handleDisconnect}
          className="flex items-center gap-2 text-white/55 hover:text-red-400 transition-colors font-accent text-[10px] tracking-[0.28em] uppercase"
        >
          <Unlink size={12} /> Disconnect
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[220px] relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/35" />
          <input
            data-testid="import-search"
            placeholder="Search name, SKU, category…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-onyx border border-white/15 pl-10 pr-3 py-2.5 w-full text-ivory focus:outline-none focus:border-gold text-sm"
          />
        </div>

        <div className="flex items-center gap-2">
          <label className="font-accent text-[10px] tracking-[0.28em] uppercase text-white/45">Currency</label>
          <div
            data-testid="import-currency"
            className="bg-onyx border border-gold/30 px-3 py-2.5 text-gold font-mono text-sm select-none"
            title="OPIKJEWELS base currency is locked to IDR"
          >
            IDR
          </div>
        </div>

        <button
          data-testid="import-refresh"
          onClick={fetchProducts}
          disabled={refreshing}
          className="flex items-center gap-2 border border-white/20 text-white/65 hover:text-gold hover:border-gold transition-colors px-3 py-2.5 font-accent text-[10px] tracking-[0.28em] uppercase disabled:opacity-50"
        >
          <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} /> Refresh
        </button>

        <button
          data-testid="import-submit"
          onClick={handleImport}
          disabled={busy || selected.size === 0}
          className="bg-gold text-black font-accent uppercase tracking-[0.28em] text-[10px] px-5 py-3 hover:bg-ivory transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          {busy ? "Importing…" : `Import Selected (${selected.size})`}
        </button>
      </div>

      {/* Products table */}
      {filtered.length === 0 ? (
        <div data-testid="import-empty" className="text-center py-16 text-white/40 text-sm border border-dashed border-white/15">
          {products.length === 0 ? "No products available on GemVault." : "No products match your search."}
        </div>
      ) : (
        <div className="border border-white/10 overflow-hidden">
          {/* Select-all row */}
          <div className="bg-onyx border-b border-white/10 px-4 py-3 flex items-center gap-3">
            <input
              data-testid="import-select-all"
              type="checkbox"
              checked={allFilteredSelected}
              onChange={toggleAll}
              className="accent-gold w-4 h-4"
            />
            <span className="font-accent text-[10px] tracking-[0.28em] uppercase text-white/45">
              {allFilteredSelected ? "Deselect All" : "Select All Visible"}
            </span>
            <span className="ml-auto text-white/40 text-xs">{filtered.length} products</span>
          </div>

          {filtered.map((p) => {
            const checked = selected.has(p.inventory_id);
            const overrideVal = priceOverrides[p.inventory_id] ?? "";
            return (
              <div
                key={p.inventory_id}
                data-testid={`import-row-${p.inventory_id}`}
                className={`flex items-center gap-4 px-4 py-3 border-b border-white/5 last:border-b-0 hover:bg-white/[0.02] transition-colors ${checked ? "bg-gold/[0.04]" : ""}`}
              >
                <input
                  data-testid={`import-checkbox-${p.inventory_id}`}
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(p.inventory_id)}
                  className="accent-gold w-4 h-4 flex-shrink-0 cursor-pointer"
                />
                <div className="w-12 h-12 bg-onyx border border-white/10 flex-shrink-0 overflow-hidden">
                  {p.photo_url ? (
                    <img src={p.photo_url} alt={p.name} className="w-full h-full object-cover" />
                  ) : null}
                </div>
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => toggle(p.inventory_id)}>
                  <div className="text-ivory truncate font-medium">{p.name}</div>
                  <div className="text-white/40 text-xs mt-0.5 flex gap-2 flex-wrap">
                    {p.sku && <span>SKU {p.sku}</span>}
                    {p.category && <span>· {p.category}</span>}
                    {p.carat ? <span>· {p.carat} ct</span> : null}
                    {p.color && <span>· {p.color}</span>}
                    {p.certification_lab && <span>· {p.certification_lab}</span>}
                    {p.origin && <span>· {p.origin}</span>}
                  </div>
                  <div className="text-white/35 text-[10px] mt-1 font-mono">
                    GemVault price · IDR {Number(p.selling_price || 0).toLocaleString("id-ID")}
                    {p.imported && p.opik_price != null && (
                      <span className="ml-3 text-emerald-400/70">
                        · Current · {Number(p.opik_price).toLocaleString()} {p.opik_currency || "USD"}
                      </span>
                    )}
                  </div>
                </div>
                {/* Custom price input — when filled, overrides the GemVault
                    selling_price on import. Empty = use GemVault value. */}
                <div className="flex items-center gap-2">
                  <div className="font-accent text-[9px] tracking-[0.22em] uppercase text-white/40 hidden sm:block">
                    Price
                  </div>
                  <input
                    data-testid={`import-price-${p.inventory_id}`}
                    type="number"
                    inputMode="decimal"
                    step="any"
                    min="0"
                    value={overrideVal}
                    onChange={(e) =>
                      setPriceOverrides((prev) => ({ ...prev, [p.inventory_id]: e.target.value }))
                    }
                    placeholder={String(Math.round(Number(p.selling_price || 0)))}
                    className="bg-onyx border border-white/15 px-3 py-2 w-32 text-ivory text-right font-mono text-sm focus:outline-none focus:border-gold"
                  />
                  <span className="text-white/40 text-[10px] font-accent tracking-[0.22em]">{currency}</span>
                </div>
                <div className="text-right hidden md:block">
                  <div className="text-white/40 text-[10px]">Qty {p.quantity ?? "—"}</div>
                </div>
                <StatusBadge imported={p.imported} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default InventoryImport;
